import * as React from "react";
import { Link } from "../../../components/router/Link";
import { AuthLayout } from "../layout";
import { UI } from "../../../../core/ui/index";
import { logger } from "@lib/common";
import { validators } from "@lib/common";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../mobx/components/index";
import { LinkTag } from "@lib/components";
import { PasswordInput } from "../components/password-input";
import {
	AuthHeading,
	AuthIntro,
	AuthSubheading,
	FooterLinks,
	FormField,
	FormInput,
	FormLabel,
	FormStack,
	SubmitButton,
	TermsField,
} from "./login.form.c";

interface Props { }
interface State {
	email: string;
	password: string;
	confirm_password: string;
	accept_terms: boolean;
	loading: boolean;
	email_confirmation_code: string;
	email_confirmation_visible: boolean;
}

const GENERIC_ERROR = "Something went wrong, try again or contact us";

@inject("store") @observer
export class AuthRegister extends MobxComponent<Props, State> {

	last_sent_confirmation: number | null;

	constructor(props: Props) {
		super(props);
		this.state = this.initial_state();
		this.last_sent_confirmation = null;
	}

	initial_state = () => {
		const { reseller } = this.injected.store;
		return {
			email: "",
			password: "",
			confirm_password: "",
			accept_terms: !reseller!.admin.terms_and_conditions,
			loading: false,
			email_confirmation_code: "",
			email_confirmation_visible: false,
		};
	}
	reset_state = () => {
		this.setState(this.initial_state());
	}

	load = (v: boolean) => {
		this.setState({ loading: v });
	}

	register_commence = async (e: React.FormEvent<HTMLFormElement>) => {

		const { store } = this.injected;
		e.preventDefault();
		const reseller = this.injected.store.reseller!;
		const reseller_id = reseller._id;
		const { email, password, confirm_password, accept_terms } = this.state;

		if (!email || !password || !confirm_password) {
			return UI.notification.error("Please fill out all the fields");
		}
		else if (!accept_terms) {
			return UI.notification.error("Please accept the terms & conditions");
		}
		else if (validators.password(password).error) {
			return UI.notification.error("Minimum password length is 5 characters");
		}
		else if (password !== confirm_password) {
			return UI.notification.error("Passwords do not match");
		}

		try {

			this.load(true);
			const data = await store.api.user_register_commence({ email, reseller_id });

			if (data.outcome) {
				this.load(false);
				return UI.notification.error(data.message);
			}

			this.setState({ loading: false, email_confirmation_visible: true });

		}
		catch (e) {
			logger.captureException(e);
			UI.notification.error(GENERIC_ERROR, { timeout: 6000 });
			this.setState({ loading: false });
		}

		return null;

	}
	register_complete = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const { store } = this.injected;
		const reseller = store.reseller!;
		const reseller_id = reseller._id;
		const { email, password, email_confirmation_code } = this.state;
		if (!email_confirmation_code) {
			return UI.notification.error("Please enter the confirmation code received via e-mail", { timeout: 6000 });
		}
		try {
			this.load(true);
			const data = await store.api.user_register_complete({ email_confirmation_code, email, password, reseller_id });
			this.load(false);
			if (data.outcome) {
				UI.notification.error(data.message);
			}
			else {
				await store.service.login(data.token, "/");
			}
		}
		catch (e) {
			logger.captureException(e);
			UI.notification.error(GENERIC_ERROR, { timeout: 6000 });
			this.load(false);
		}
		return null;
	}
	resend_email_confirmation = async () => {
		const { store } = this.injected;
		try {
			const time_elapsed = Date.now() - (this.last_sent_confirmation || 0);
			if (time_elapsed < (1000 * 10)) {
				const seconds_left = Math.round((time_elapsed - (1000 * 10)) / 1000);
				return UI.notification.error(`Please wait ${-seconds_left} seconds before re-sending`);
			}

			this.load(true);
			const data = await store.api.user_register_commence({
				email: this.state.email,
				reseller_id: store.reseller!._id,
			});
			if (data.outcome) {
				UI.notification.error(data.message);
			}
			else {
				UI.notification.success(`Confirmation code re-sent to ${this.state.email}`);
				this.last_sent_confirmation = Date.now();
			}
			this.load(false);
		}
		catch (e) {
			logger.captureException(e);
			UI.notification.error(GENERIC_ERROR, { timeout: 6000 });
			this.load(false);
		}
		return null;
	}

	onChange = <T extends keyof State>(e: React.ChangeEvent<HTMLInputElement>) => {
		const newState = UI.helpers.handleChange(e);
		this.setState(newState as { [P in T]: State[P]; });
	}

	renderSubmitLabel = (loadingLabel: string, idleLabel: string) => {
		const { loading } = this.state;
		if (loading) {
			return (
				<>
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
						<circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
						<path fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" opacity="0.75" />
					</svg>
					{loadingLabel}
				</>
			);
		}
		return idleLabel;
	}

	render_commence = () => {
		const { onChange } = this;
		const { reseller } = this.injected.store;
		const { loading } = this.state;
		const hasTerms = !!reseller!.admin.terms_and_conditions;

		return (
			<>
				<AuthIntro>
					<AuthHeading>Create an account</AuthHeading>
					<AuthSubheading>Sign up to get started.</AuthSubheading>
				</AuthIntro>

				<form onSubmit={this.register_commence}>
					<FormStack>
						<FormField>
							<FormLabel htmlFor="register-email">Email Address</FormLabel>
							<FormInput
								id="register-email"
								type="email"
								name="email"
								placeholder="you@example.com"
								value={this.state.email}
								onChange={onChange}
								autoComplete="email"
								required={true}
								disabled={loading}
							/>
						</FormField>

						<FormField>
							<FormLabel htmlFor="register-password">Password</FormLabel>
							<PasswordInput
								id="register-password"
								name="password"
								placeholder="Enter your password"
								value={this.state.password}
								onChange={onChange}
								autoComplete="new-password"
								required={true}
								disabled={loading}
							/>
						</FormField>

						<FormField>
							<FormLabel htmlFor="register-confirm-password">Confirm password</FormLabel>
							<PasswordInput
								id="register-confirm-password"
								name="confirm_password"
								placeholder="Enter your password"
								value={this.state.confirm_password}
								onChange={onChange}
								autoComplete="new-password"
								required={true}
								disabled={loading}
							/>
						</FormField>

						{hasTerms && (
							<TermsField>
								<input
									id="accept-terms"
									type="checkbox"
									name="accept_terms"
									checked={this.state.accept_terms}
									onChange={onChange}
									disabled={loading}
								/>
								<span>
									Accept Terms &amp; Conditions -{" "}
									<LinkTag target="_blank" href={reseller!.admin.terms_and_conditions}>View</LinkTag>
								</span>
							</TermsField>
						)}

						<SubmitButton type="submit" disabled={loading}>
							{this.renderSubmitLabel("Continuing…", "Continue")}
						</SubmitButton>

						<FooterLinks>
							<p>Already have an account? <Link to="/login">Login</Link></p>
						</FooterLinks>
					</FormStack>
				</form>
			</>
		);
	}
	render_complete = () => {
		const { loading } = this.state;

		return (
			<>
				<AuthIntro>
					<AuthHeading>Confirm your email</AuthHeading>
					<AuthSubheading>A 5-6 digit code was sent to your e-mail. Enter it below to proceed.</AuthSubheading>
				</AuthIntro>

				<form onSubmit={this.register_complete}>
					<FormStack>
						<FormField>
							<FormLabel htmlFor="email-confirmation-code">Confirmation code</FormLabel>
							<FormInput
								id="email-confirmation-code"
								type="text"
								name="email_confirmation_code"
								placeholder="Enter confirmation code"
								value={this.state.email_confirmation_code}
								onChange={this.onChange}
								required={true}
								disabled={loading}
							/>
						</FormField>

						<SubmitButton type="submit" disabled={loading}>
							{this.renderSubmitLabel("Completing…", "Complete Registration")}
						</SubmitButton>

						<FooterLinks>
							<p>
								Didn&apos;t receive the code?<br />
								<LinkTag onClick={this.resend_email_confirmation}>Resend e-mail confirmation code</LinkTag> |{" "}
								<LinkTag onClick={this.reset_state}>Start again</LinkTag>
							</p>
						</FooterLinks>
					</FormStack>
				</form>
			</>
		);
	}

	render() {
		const { email_confirmation_visible } = this.state;
		return (
			<AuthLayout loading={this.state.loading}>
				{email_confirmation_visible ? this.render_complete() : this.render_commence()}
			</AuthLayout>
		);
	}

}
